/**
 * cpSurveyApi.js
 *
 * Fetches CorrMonitor CP Survey jobs from the /assetview/cp-survey-jobs
 * endpoint and normalises them into the same shape used by CorrLogix
 * "line" objects, so they can be merged and rendered by the All Projects page.
 */

import { ajaxNew as ajax } from '../../../../../scanline/utils/ajax';
import { cacheKey, cachedCollection } from '../../../../../scanline/utils/dataCache';
import { APPROVAL_MODE } from '../../../../../assetView/constants';

const PROJECT_COLLECTION_MAX_AGE_MS = 5 * 60 * 1000;

// ── Retry helper ──────────────────────────────────────────────────

/**
 * Retry a thunk up to `attempts` times with exponential back-off.
 * The first failure triggers an 800 ms delay; each retry doubles it.
 */
const withRetry = (fn, attempts = 3, baseDelayMs = 800) => {
	const run = async (remaining, delay) => {
		try {
			return await fn();
		} catch (err) {
			if (remaining <= 1) {throw err;}
			await new Promise(res => setTimeout(res, delay));
			return run(remaining - 1, delay * 2);
		}
	};
	return run(attempts, baseDelayMs);
};

// ── Raw API call ──────────────────────────────────────────────────

/**
 * Fetch CP Survey jobs from CorrMonitor.
 * Automatically retried up to 3 times on failure (intermittent ES timeouts).
 *
 * @param {object} opts
 * @param {string|null} opts.clientName  Optional customer name to filter by.
 *                                       Omit (or pass null) for internal users
 *                                       who see all customers.
 * @param {string} opts.approval  Approval scope ('a' = approved, 'u' = unapproved).
 *                                Defaults to approved so the All Projects page only
 *                                surfaces approved CP data (excludes pending/
 *                                unapproved-only assets).
 * @returns {Promise<Array>}  Raw job objects from the lambda.
 */
export const fetchCpSurveyJobs = ({ clientName, approval = APPROVAL_MODE.approved } = {}) => {
	const body = { approval };
	if (clientName) { body.clientName = clientName; }
	return withRetry(() =>
		ajax({
			url:    '/cp-survey-jobs',
			data:   JSON.stringify(body),
			method: 'POST',
			aip:    'assetview'
		})
	).then(extractCpSurveyJobs);
};

const extractCpSurveyJobs = res => {
	if (Array.isArray(res)) return res;
	if (Array.isArray(res && res.jobs)) return res.jobs;
	if (Array.isArray(res && res.items)) return res.items;
	if (typeof (res && res.body) === 'string') {
		try {
			return extractCpSurveyJobs(JSON.parse(res.body));
		} catch (err) {
			return [];
		}
	}
	return [];
};

const uniqueCustomerNames = customers => [
	...new Set((customers || [])
		.map(customer => String(customer || '').trim())
		.filter(Boolean))
];

const dedupeCpSurveyJobs = jobs => {
	const jobsByKey = new Map();
	jobs.forEach(job => {
		const key = [job.customer || '', job.jobNumVendor || job.name || ''].join('::');
		if (!jobsByKey.has(key)) {
			jobsByKey.set(key, job);
		}
	});
	return [...jobsByKey.values()];
};

export const fetchCpSurveyJobsForCustomers = ({
	customers = [],
	isInternal = false,
	approval = APPROVAL_MODE.approved
} = {}) => {
	const customerNames = uniqueCustomerNames(customers);
	if (isInternal) {return fetchCpSurveyJobs({ approval });}

	if (customerNames.length === 1) {
		return fetchCpSurveyJobs({ clientName: customerNames[0], approval });
	}

	if (customerNames.length > 1 && !isInternal) {
		return Promise.all(customerNames.map(clientName => fetchCpSurveyJobs({ clientName, approval })))
			.then(results => dedupeCpSurveyJobs(results.reduce((all, jobs) => all.concat(jobs), [])));
	}

	return fetchCpSurveyJobs({ approval });
};

// ── Cache-backed loading (IndexedDB) ──────────────────────────────

/**
 * Build a stable cache key for the CP-survey fetch scope so each customer
 * scope is cached independently.
 */
const cpSurveyScopeSuffix = ({ customers = [], isInternal = false } = {}) => {
	if (isInternal) {return 'all';}
	const names = uniqueCustomerNames(customers);
	if (names.length === 1) {return `cust:${names[0]}`;}
	if (names.length > 1 && !isInternal) {return `custs:${names.join('|')}`;}
	return 'all';
};

/**
 * Cache-first CP-survey-jobs loader.  Paints the cached jobs instantly via
 * `onCached`, then revalidates from the network and replaces the cache.
 *
 * The CorrMonitor list endpoint has no delta/updatedSince support, so this is
 * a stale-while-revalidate full download: instant render from cache, fresh
 * data on the next tick.  Jobs are cached already-normalised.
 */
export const fetchCpSurveyJobsCached = ({
	customers = [],
	isInternal = false,
	approval = APPROVAL_MODE.approved,
	onCached
} = {}) =>
	cachedCollection({
		key:   cacheKey('cp-survey-v2', approval, cpSurveyScopeSuffix({ customers, isInternal })),
		getId: job => job.id,
		maxAgeMs: PROJECT_COLLECTION_MAX_AGE_MS,
		fetchFull: () =>
			fetchCpSurveyJobsForCustomers({ customers, isInternal, approval })
				.then(jobs => jobs.map(normalizeCpSurveyJob)),
		onCached
	});

const CP_SURVEY_JOB_ASSETS_CACHE_LIMIT = 12;
const cpSurveyJobAssetsCache = new Map();

const rememberCpSurveyJobAssets = (cacheId, value) => {
	if (cpSurveyJobAssetsCache.has(cacheId)) {
		cpSurveyJobAssetsCache.delete(cacheId);
	}
	cpSurveyJobAssetsCache.set(cacheId, value);
	while (cpSurveyJobAssetsCache.size > CP_SURVEY_JOB_ASSETS_CACHE_LIMIT) {
		const oldestKey = cpSurveyJobAssetsCache.keys().next().value;
		cpSurveyJobAssetsCache.delete(oldestKey);
	}
};

export const fetchCpSurveyJobAssets = async ({
	clientName,
	jobNumVendor,
	pageSize = 1000,
	// Only approved assets are surfaced for CP data (parity with the CP job
	// list and the CorrMonitor page); excludes pending / unapproved-only assets.
	approval = APPROVAL_MODE.approved
}) => {
	const cacheId = JSON.stringify({
		clientName: clientName || '',
		jobNumVendor: jobNumVendor || '',
		pageSize,
		approval
	});
	if (cpSurveyJobAssetsCache.has(cacheId)) {
		return cpSurveyJobAssetsCache.get(cacheId);
	}

	const fetchPage = from =>
		ajax({
			url:    '/api/v1.0/corrmonitor/list-job-assets',
			data:   JSON.stringify({
				clientName,
				jobNumVendor,
				from,
				size: pageSize,
				approval
			}),
			method: 'POST',
			aip:    'assetview'
		});

	const fetchPages = async (from = 0, items = []) => {
		const res = await fetchPage(from);
		const pageItems = Array.isArray(res && res.items) ? res.items : [];
		const nextItems = [...items, ...pageItems];
		const nextTotal = res && Number(res.total) ? Number(res.total) : nextItems.length;
		const nextFrom = res && res.nextFrom !== undefined && res.nextFrom !== null
			? res.nextFrom
			: null;
		if (nextFrom === null) {
			return { items: nextItems, total: nextTotal };
		}
		return fetchPages(nextFrom, nextItems);
	};

	const request = fetchPages()
		.then(result => {
			rememberCpSurveyJobAssets(cacheId, Promise.resolve(result));
			return result;
		})
		.catch(err => {
			cpSurveyJobAssetsCache.delete(cacheId);
			throw err;
		});

	rememberCpSurveyJobAssets(cacheId, request);
	return request;
};

// ── URL builder ───────────────────────────────────────────────────

/**
 * Derive an appropriate CorrMonitor map viewport (zoom + centre) from the
 * job's bounding box or centroid so that "Open Project" opens zoomed to the
 * full survey area rather than the world-default view.
 *
 * The CorrMonitor route is `/CorrMonitor/:zoom?/:lat?/:lon?/` and
 * Map.jsx reads these path params in componentDidMount to call setViewport().
 *
 * Zoom thresholds are calibrated for a ~1000 px wide map pane.
 *
 * @param {number[]|null} location  [lon, lat] centroid
 * @param {number[]|null} bbox      [minLon, minLat, maxLon, maxLat]
 * @returns {{ zoom: number, lat: number, lon: number }|null}
 */
export const deriveViewport = (location, bbox) => {
	const validBbox =
		Array.isArray(bbox) &&
		bbox.length === 4 &&
		bbox.every(n => typeof n === 'number' && Number.isFinite(n)) &&
		bbox[2] > bbox[0] &&
		bbox[3] > bbox[1];

	const center = validBbox
		? [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] // [centerLon, centerLat]
		: (Array.isArray(location) && location.length === 2 ? location : null);

	if (!center) return null;
	const [lon, lat] = center;

	let zoom = 12; // sensible default for centroid-only
	if (validBbox) {
		const maxSpan = Math.max(Math.abs(bbox[2] - bbox[0]), Math.abs(bbox[3] - bbox[1]));
		if      (maxSpan <= 0.05) zoom = 14;
		else if (maxSpan <= 0.15) zoom = 13;
		else if (maxSpan <= 0.5)  zoom = 11;
		else if (maxSpan <= 1.5)  zoom = 10;
		else if (maxSpan <= 4)    zoom = 8;
		else                      zoom = 7;
	}

	return { zoom, lat, lon };
};

/**
 * Build a /CorrMonitor URL that pre-selects the Customer and Job Number
 * dropdowns via query-string params and opens the map at the job's extent.
 *
 * URL form: /CorrMonitor/{zoom}/{lat}/{lon}/?tab=map&Customer=...&JobNumber=...
 *
 * routes.js (decodeUrl) merges Customer/JobNumber into `selected` so that
 * SearchParms.js can call setSelection() before the first getList() call.
 * Map.jsx reads the path params in componentDidMount and calls setViewport().
 */
const buildCorrMonitorUrl = (customer, jobNumVendor, { location, bbox } = {}) => {
	const query = ['tab=map'];
	if (customer) {
		// The jobNumVendor here is the BASE key (everything before the first '-'),
		// e.g. "J12345" representing variants "J12345-1", "J12345-2", etc.
		// processCategories (backend) expands the selected base key to exact and
		// hyphen-prefix matches so all variants are returned.
		query.push(`Customer=${encodeURIComponent(customer)}`);
	}
	if (jobNumVendor) {
		query.push(`JobNumber=${encodeURIComponent(jobNumVendor)}`);
	}

	const viewport = deriveViewport(location, bbox);
	if (viewport) {
		const { zoom, lat, lon } = viewport;
		return `/CorrMonitor/${zoom}/${lat.toFixed(6)}/${lon.toFixed(6)}/?${query.join('&')}`;
	}
	return `/CorrMonitor?${query.join('&')}`;
};

// ── Data normalisation ────────────────────────────────────────────

/**
 * Convert a raw CP Survey job record (from listCpSurveyJobs lambda) into
 * a shape that matches the CorrLogix "line" objects stored in Redux, so
 * ProjectCard / SummaryModal can render them without special-casing every
 * field.
 *
 * Fields that differ from CorrLogix lines:
 *   source          → 'corrmonitor'  (distinguishes origin)
 *   assetCount      → total assets + structures for the job
 *   totalcount      → same as assetCount (mapped for ProjectCard metrics)
 *   under850Count   → CorrMonitor non-compliant CP count
 *   anomalyCount    → same CP anomaly count displayed on project cards
 *   bccount         → same count mapped for existing shared CorrLogix metrics
 *   bcpercentage    → % non-compliant CP readings (pre-calculated on backend)
 *   corrMonitorUrl  → URL to open job in CorrMonitor (with Customer + JobNumber pre-selected)
 */
// ── CP project id helpers ─────────────────────────────────────────
// CP projects include a customer-qualified identity (see the
// `id` field built in normalizeCpSurveyJob).  These helpers keep the
// prefix convention in one place for the CorrMonitor project page.

export const CP_PROJECT_ID_PREFIX = 'corrmonitor:';

/** Build a customer-qualified CP id, retaining the legacy job-only form. */
export const buildCorrMonitorProjectId = (jobNumVendor, customer) =>
	`${CP_PROJECT_ID_PREFIX}${customer ? `${encodeURIComponent(customer)}::` : ''}${jobNumVendor}`;

/** Extract the job number from a CP project id (tolerates a bare job number). */
export const stripCorrMonitorId = projectId => {
	const value = String(projectId || '');
	const identity = value.startsWith(CP_PROJECT_ID_PREFIX)
		? value.slice(CP_PROJECT_ID_PREFIX.length)
		: value;
	const separator = identity.indexOf('::');
	return separator === -1 ? identity : identity.slice(separator + 2);
};

export const getCpProjectCustomer = projectId => {
	const identity = String(projectId || '').replace(/^corrmonitor:/, '');
	const separator = identity.indexOf('::');
	return separator === -1 ? '' : decodeURIComponent(identity.slice(0, separator));
};

/**
 * Build the single-project CorrMonitor route path from either a CP project id
 * (`corrmonitor:<jobNumVendor>`) or a bare job number.  The `corrmonitor:`
 * prefix is stripped so the URL is `/CorrMonitor/CP/Project/<jobNumVendor>`
 * (clean, prefix-free).  The project page still tolerates the legacy prefixed
 * form, so pre-existing bookmarks keep working.
 */
export const buildCorrMonitorProjectPath = idOrJobNumber =>
	`/CorrMonitor/CP/Project/${encodeURIComponent(stripCorrMonitorId(idOrJobNumber))}${
		getCpProjectCustomer(idOrJobNumber) ? `?Customer=${encodeURIComponent(getCpProjectCustomer(idOrJobNumber))}` : ''
	}`;

export const CP_PROJECT_REFRESH_PARAM = 'refreshProjectCache';

export const withCorrMonitorProjectRefresh = path => {
	if (!path || path === '#') {return path;}
	const separator = path.indexOf('?') === -1 ? '?' : '&';
	return `${path}${separator}${CP_PROJECT_REFRESH_PARAM}=1`;
};

export const buildCorrMonitorProjectRefreshPath = idOrJobNumber =>
	withCorrMonitorProjectRefresh(buildCorrMonitorProjectPath(idOrJobNumber));

const cleanDisplayValue = value => {
	if (value === null || value === undefined) {return null;}
	const text = String(value).trim();
	if (!text || text.toLowerCase() === 'not set') {return null;}
	return text;
};

const firstCleanDisplayValue = (...values) =>
	values.map(cleanDisplayValue).find(Boolean) || null;

// Card titles are intentionally compact. Once a description is this long,
// keeping the operating area on its own line prevents it from being clipped.
const LONG_CP_PROJECT_DESCRIPTION_LENGTH = 48;

const getCpProjectTitleParts = ({ projDesc, operatingArea, operatingAreaName } = {}) => {
	const description = cleanDisplayValue(projDesc);
	const operatingAreaDisplay = cleanDisplayValue(operatingArea || operatingAreaName);
	return { description, operatingAreaDisplay };
};

// The CP project identity shown throughout the application. Keep the raw
// project description separate from the generic description field because the
// latter may be populated from other metadata when projDesc is absent.
export const getCpProjectDisplayTitle = line => {
	const { description, operatingAreaDisplay } = getCpProjectTitleParts(line);

	if (description && operatingAreaDisplay) {
		return `${description} / ${operatingAreaDisplay}`;
	}
	return description || operatingAreaDisplay || null;
};

// UI headings use a line break for long descriptions so their operating area
// remains visible without a separating slash.
export const getCpProjectTitleForLayout = line => {
	const { description, operatingAreaDisplay } = getCpProjectTitleParts(line);
	if (description && operatingAreaDisplay && description.length > LONG_CP_PROJECT_DESCRIPTION_LENGTH) {
		return `${description}\n${operatingAreaDisplay}`;
	}
	return getCpProjectDisplayTitle(line);
};

export const normalizeCpSurveyJob = job => {
	const startDate = job.surveyStart ? new Date(job.surveyStart) : undefined;
	const endDate   = job.surveyEnd   ? new Date(job.surveyEnd)   : undefined;

	// Numeric coercion helper — null when the backend omitted the field
	// (older lambda versions without the potential-band aggregations).
	const numOrNull = v => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
	const pctOf = (count, total) =>
		(count !== null && total !== null && total > 0 ? (count / total) * 100 : null);

	const assetTotal             = numOrNull(job.assetCount);
	const over850Count           = numOrNull(job.over850Count);
	const between850And1200Count = numOrNull(job.between850And1200Count);
	const under1200Count         = numOrNull(job.under1200Count);
	const unknownCount           = numOrNull(job.unknownCount);
	// Newer backends send workflow compliance:
	// required Structure On/Off, then Off <= −850 mV or >100 mV polarization
	// shift. Older backends only sent band counts; keep that fallback so cached
	// data and staggered deployments still render.
	const compliantCount = numOrNull(job.compliantCount) !== null
		? numOrNull(job.compliantCount)
		: (between850And1200Count !== null && under1200Count !== null
			? between850And1200Count + under1200Count
			: null);
	const compliancePercentage = numOrNull(job.compliancePercentage) !== null
		? numOrNull(job.compliancePercentage)
		: pctOf(compliantCount, assetTotal);
	const overprotectedCount = numOrNull(job.overprotectedCount) !== null
		? numOrNull(job.overprotectedCount)
		: under1200Count;
	const overprotectedPercentage = numOrNull(job.overprotectedPercentage) !== null
		? numOrNull(job.overprotectedPercentage)
		: pctOf(overprotectedCount, assetTotal);
	const deficiencyCount = numOrNull(job.deficiencyCount);
	const deficiencyPercentage = numOrNull(job.deficiencyPercentage) !== null
		? numOrNull(job.deficiencyPercentage)
		: pctOf(deficiencyCount, assetTotal);

	return {
		// ── Identity ────────────────────────────────────────────
		id:       buildCorrMonitorProjectId(job.jobNumVendor, job.customer),
		name:     job.jobNumVendor,
		customer: job.customer || '',
		source:   'corrmonitor',

		// ── Survey metadata ─────────────────────────────────────
		projectName:       job.projectName       || null,
		facilityName:      job.facilityName      || null,
		operatingArea:     job.operatingAreaName  || null,
		projDesc:          cleanDisplayValue(job.projDesc),
		description: firstCleanDisplayValue(
			job.description,
			job.projectDescription,
			job.projDesc,
			job.assetDescription
		),
		lineLicenseNumber: job.lineLicenseNumber  || null,

		// ── Primary survey (mirrors CorrLogix primarySurvey shape) ──
		primarySurvey: {
			type:      'CP_SURVEY',
			// Default to ACTIVE so status filter works;
			// CorrMonitor jobs have no traditional lifecycle status.
			status:    'ACTIVE',
			start:     startDate,
			end:       endDate,
			jobId:     job.jobNumVendor,
			strJobId:  job.jobNumVendor,
			surveyGuid: null
		},

		// ── Metrics ─────────────────────────────────────────────
		under850Count:       job.under850Count != null ? job.under850Count : job.bccount,
		under850Percentage:  job.under850Percentage != null ? job.under850Percentage : job.bcpercentage,
		underprotectedAreaPercentage:
			job.underprotectedAreaPercentage != null
				? job.underprotectedAreaPercentage
				: job.under850Percentage,
		anomalyCount:        job.anomalyCount != null ? job.anomalyCount : job.bccount,
		assetCount:     job.assetCount,
		totalcount:     job.assetCount,        // re-use totalcount slot for card metric
		bccount:        job.bccount   != null ? job.bccount   : null,
		bcpercentage:   job.bcpercentage != null ? job.bcpercentage : null,

		// ── Potential-band counts (structure-to-soil readings) ──
		// over −850 → not protected; −850 to −1200 → protected;
		// under −1200 → over protected; unknown → no usable reading.
		over850Count,
		between850And1200Count,
		under1200Count,
		unknownCount,
		// Workflow-derived compliance; falls back to potential bands on older
		// cached/backend payloads.
		compliantCount,
		compliancePercentage,
		nonCompliantCount: job.under850Count != null ? job.under850Count : job.bccount,
		// Over-protected (under −1200 mV) — read by SummaryModal
		overprotectedCount,
		overprotectedPercentage,
		overprotectedAreaPercentage:
			job.overprotectedAreaPercentage != null
				? job.overprotectedAreaPercentage
				: overprotectedPercentage,
		geotechnicalAreaPercentage:
			job.geotechnicalAreaPercentage != null
				? job.geotechnicalAreaPercentage
				: job.geotechnicalPercentage,
		cpAnomalyAreaCount:
			job.cpAnomalyAreaCount != null ? job.cpAnomalyAreaCount : job.cpAnomalyFlagCount,
		coatingAnomalyAreaCount:
			job.coatingAnomalyAreaCount != null
				? job.coatingAnomalyAreaCount
				: job.coatingAnomalyFlagCount,
		geotechnicalAnomalyAreaCount:
			job.geotechnicalAnomalyAreaCount != null
				? job.geotechnicalAnomalyAreaCount
				: job.geotechnicalAnomalyFlagCount,
		surveyLengthMiles:
			job.surveyLengthMiles != null ? job.surveyLengthMiles : job.surveyMiles,
		// Recorded asset deficiencies (deficiency / deficiencyType fields)
		deficiencyCount,
		deficiencyPercentage,
		// Per-licence asset breakdown from the ES sub-aggregation
		assetBreakdown: Array.isArray(job.assets) ? job.assets : [],
		structureBreakdown: Array.isArray(job.structures) ? job.structures : [],
		// Per-facility asset breakdown ([{ name, count }]).  Older backends only
		// return the single representative facilityName — fall back to that so
		// the card facility chip / summary list still render something useful.
		facilities: Array.isArray(job.facilities) && job.facilities.length > 0
			? job.facilities
			: (job.facilityName ? [{ name: job.facilityName, count: null }] : []),

		// ── Geographic location ──────────────────────────────────
		// [lon, lat] centroid of the job's geo-located assets.  Used by
		// ProjectCard to render a static map snapshot via /markermap.  Null when
		// no asset in the job has a location (snapshot falls back to neutral bg).
		location: Array.isArray(job.location) && job.location.length === 2
			? job.location
			: null,
		// [minLon, minLat, maxLon, maxLat] bounding box of all located assets.
		// Used to render the full survey extent on the card thumbnail and to seed
		// the CorrMonitor map viewport when "Open Project" is clicked.
		bbox: Array.isArray(job.bbox) && job.bbox.length === 4
			? job.bbox
			: null,

		// ── Additional survey stats ─────────────────────────────
		surveyCount:       undefined,
		recentSurveyCount: undefined,
		jobCount:          undefined,
		surveyTypesList:   '',
		tags:              null,

		// ── CorrLogix fields — not applicable ───────────────────
		jobs:            [],
		scanlineEnabled: false,

		// ── CorrMonitor deep-link ────────────────────────────────
		// Pre-selects Customer and Job Number dropdowns via query-string params
		// and seeds the map viewport for the project's Map view.
		corrMonitorUrl: buildCorrMonitorUrl(job.customer || '', job.jobNumVendor, {
			location: Array.isArray(job.location) && job.location.length === 2 ? job.location : null,
			bbox:     Array.isArray(job.bbox)     && job.bbox.length     === 4 ? job.bbox     : null
		})
	};
};
